/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import { StateParams } from '@uirouter/core';

import {
    Component,
    Inject,
    Input,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { L10nService } from '@vmw/ngx-vip';
import { Timeframe } from 'ajs/js/services/Timeframe';
import {
    calculateStep,
    convertTimeToTTimestampFormatFromApi,
} from 'ng/modules/diagram/components/avi-bar-graph/avi-bar-graph.utils';
import {
    VirtualServiceStateService,
} from 'ng/modules/virtual-service/services/virtual-service-state.service';

import {
    ILogsStates,
    LogsStore,
    TApiLoopsInProgressStatus,
    TCustomTimeframeStatus,
    TErrorStates,
    TFilterProps,
    TLogsPageType,
    TTableProps,
} from '../../services/logs.store';
import {
    LogsTimeframeOptions,
    LogsTimeframesService,
} from '../../services/logs-timeframes.service';
import { LogsRefreshService } from '../../services/logs-refresh.service';
import { LogsDownloadService } from '../../services/logs-download.service';
import {
    FilterOperatorType,
    ILogsStateParams,
} from '../../logs.types';
import {
    defaultPageNumber,
    defaultPageSize,
} from '../../constants/default-values.constants';
import { convertFilterObjectToQueryString } from '../../utils/logs-filters.utils';

import * as l10n from './logs-page.l10n';
import './logs-page.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @abstract
 * @description
 *     An abstraction component of the main page base layer for logs, used by VS, Pool and Event logs.
 *     Children log components (graph, table, etc.) use shared data from module level LogsStore.
 *     The injected services should be substituted with parent scope services.
 *     Should never be used as an end component.
 * @author Akul Aggarwal, Alex Klyuev, Zhiqian Liu
 */
@Component({
    selector: 'logs-page',
    templateUrl: './logs-page.component.html',
    providers: [LogsRefreshService, LogsDownloadService],

})
export class LogsPageComponent implements OnInit, OnDestroy {
    // TODO: may need to be removed going forward
    /**
     * Type indicating what kind of log page this component is used for. Default to VS page.
     */
    @Input()
    public logsPageType: TLogsPageType = 'vs';

    public readonly l10nKeys = l10nKeys;

    /**
     * Email address for users to send feedback to.
     */
    public readonly feedbackEmailAddress = 'avi-product-feedback@broadcom.com';

    /**
     * Documentation link to learn more about timeouts.
     */
    public readonly timeoutsDocLink = 'https://avinetworks.com/' +
        'docs/22.1/architectural-overview/applications/virtual-services/vs-logs/#log-navigation';

    /**
     * Control whether to show banner about tech-preview/legacy view.
     */
    public showRevertPageBanner = true;

    /**
     * Currently selected VS.
     * TODO: change "any" to the VirtualService class once it's been converted to Typescript.
     */
    private virtualService?: any;

    constructor(
        l10nService: L10nService,
        // must be provided by the parent component
        public readonly logsStore: LogsStore<ILogsStates>,
        @Inject('Timeframe')
        private readonly timeframe: Timeframe,
        private readonly virtualServiceStateService: VirtualServiceStateService,
        private readonly logsTimeframesService: LogsTimeframesService,
        logsDownloadService: LogsDownloadService,
        public readonly logsRefreshService: LogsRefreshService,
        @Inject('$stateParams')
        private readonly $stateParams: StateParams,
    ) {
        l10nService.registerSourceBundles(dictionary);

        // Temporary workaround to "custom" being in the url until Timeframe
        // component is upgraded
        if (this.timeframe.selected().key === LogsTimeframeOptions.CUSTOM) {
            this.timeframe.set(LogsTimeframeOptions.FIFTEEN_MINUTES);
        }

        // Subscribe to the timeframe service
        // @ts-expect-error will be handled when timeframe upgraded to angular
        this.timeframe.on('change', this.setDurationAndStepApiParams);

        // Subscribe to Download service for download all logs action.
        logsDownloadService.downloadAllLogs$.subscribe(() => this.logsStore.downloadAllLogs());
    }

    /** @override */
    public ngOnInit(): void {
        // TODO: move out to vs-logs-page.component with AV-234124
        if (this.logsPageType === 'vs') {
            // if VS logs, use the VS from the VS detail page, since it's the only source of truth for the log traffic
            this.virtualService = this.virtualServiceStateService.virtualService;

            // Subscribe to changes in the currently inspected VS.
            this.virtualService.on('itemSaveSuccess', this.setVsProps);
        }

        this.setEssentialStates();
    }

    /**
     * @override Manually unsubscribe where necessary.
     */
    public ngOnDestroy(): void {
        // TODO: move out to vs-logs-page.component with AV-234124
        if (this.logsPageType === 'vs') {
            this.virtualService.unbind('itemSaveSuccess', this.setVsProps);
        }

        // @ts-expect-error will be handled when timeframe upgraded to angular
        this.timeframe.unbind('change', this.setDurationAndStepApiParams);
    }

    /**
     * Hide banner upon close action.
     */
    public hideRevertPageBanner(): void {
        this.showRevertPageBanner = false;
    }

    /**
     * Hide timeout message upon close action.
     */
    public hideTimeoutBanner(): void {
        this.logsStore.setisTimeoutBannerEnabled(false);
    }

    /**
     * Hide error message upon close action.
     */
    public hideErrorBanner(): void {
        this.logsStore.setisErrorBannerEnabled(false);
    }

    /**
     * Reload logs page.
     */
    // eslint-disable-next-line class-methods-use-this
    public reloadPage(): void {
        window.location.reload();
    }

    /**
     * Get the duration value from the timeframe service.
     */
    private getDuration(): number {
        const timeframeKey = this.timeframe.selected().key;

        if (timeframeKey === 'custom') {
            return 0;
        }

        // TODO: remove when VS Logs timeframe only uses default
        // Must perform this check to avoid a console error until Timeframe Component is updated
        return this.logsTimeframesService.getTimeframeProps(timeframeKey)?.range as number || 900;
    }

    /**
     * Set the duration and step in the component store upon timeframe change.
     */
    private setDurationAndStepApiParams = (): void => {
        if (this.getDuration() === 0) {
            return;
        }

        this.logsStore.setDurationAndStepApiParams(
            this.getDuration(),
            calculateStep(this.getDuration()),
        );
    };

    // TODO: move out to vs-logs-page.component with AV-234124
    /**
     * Set state props that are based on the VS.
     */
    private setVsProps = (): void => {
        this.logsStore.setVsProps(this.virtualService);
    };

    /**
     * Load page with pre-populated search filters, if any exist.
     */
    private initFilters = (): string[] => {
        const { initFilterParams } = this.$stateParams;

        const queryStrings: string[] = [];

        Object.entries(initFilterParams).forEach(([property, value]: [string, string]) => {
            queryStrings.push(convertFilterObjectToQueryString({
                property,
                operator: FilterOperatorType.EQUAL_TO,
                value,
            }));
        });

        return queryStrings;
    };

    /**
     * Set the essential states of the Logs module.
     * Set without vs props as the parent component should have set it already before reaching this method.
     */
    private setEssentialStates(): void {
        const filters = this.initFilters();

        const partialApiParams: ILogsStateParams = {
            // TODO: update to 15 min default when timeframe service is upgraded
            duration: this.getDuration(),
            end: convertTimeToTTimestampFormatFromApi(Date.now()),
            step: calculateStep(this.getDuration()),
            page: defaultPageNumber,
            page_size: defaultPageSize,
            filters,
        };

        const tableProps: TTableProps = {
            customEndTime: String(),
            customDuration: Number(),
            isLoading: false,
            selectedLogs: [],
        };

        const filterProps: TFilterProps = {
            hasFilters: Boolean(filters.length),
            savedSearches: {},
            isCurrentFilterSaved: false,
        };

        const apiLoopsInProgressStatus: TApiLoopsInProgressStatus = {
            isGraphApiLoopInProgress: false,
            isTableApiLoopInProgress: false,
        };

        const customTimeframeStatus: TCustomTimeframeStatus = {
            isPageCustomTimeframe: false,
            isTableCustomTimeframe: false,
        };

        const errorStates: TErrorStates = {
            hasError: false,
            isErrorBannerEnabled: true,
            isTimedOut: false,
            isTimeoutBannerEnabled: true,
        };

        this.logsStore.setLogsPageType(this.logsPageType);
        this.logsStore.setPartialApiParams(partialApiParams);
        this.logsStore.setTotalLogCount(0);
        this.logsStore.setListLogCount(0);
        this.logsStore.setTableProps(tableProps);
        this.logsStore.setFilterProps(filterProps);
        this.logsStore.setApiLoopsInProgressStatus(apiLoopsInProgressStatus);
        this.logsStore.setCustomTimeframeStatus(customTimeframeStatus);
        this.logsStore.setErrorStates(errorStates);

        this.logsStore.initSavedSearches();

        // TODO: delete with vs-logs-page.component with AV-234124, as these vs props should be set in the parent
        // vs-logs-page component with this.vsLogsStore.setVsProps(this.virtualService) as in pool-logs-page component
        // if (this.logsPageType === 'vs') {

        // TODO set these in the vs-logs-page component with with AV-234124
        // adf: defaultShowSignificantLogs,
        // udf: defaultShowNonSignificantLogs,
        // nf: defaultShowNonSignificantLogs,

        //     const vsConfig = getVsConfig(this.virtualService);
        //     const { logsType, vsUuid } = vsConfig;

        //     // TODO: with AV-234124,
        //     essentialStates.vsConfig = vsConfig;
        //     essentialStates.apiParams.type = logsType;
        //     essentialStates.apiParams.virtualservice = vsUuid;

        //     this.logsStore.setState(state => ({
        //         ...state,
        //         ...essentialStates,
        //         apiParams: {
        //             ...state.apiParams,
        //             ...partialApiParams,
        //             virtualservice: vsUuid,
        //         },
        //     }));
        // }
    }
}
